/**
 * Turning a start time into the counter on screen.
 *
 * React-free on purpose, like `projects/lib.ts` next door: this is the
 * arithmetic the whole feature is read through, and it should be testable
 * without mounting anything.
 *
 * THE COUNTER IS NOT THE RECORD. What gets written when a clock stops is the
 * server's own reading of the same start time, so these functions exist to draw
 * a number that AGREES with that one - which is the entire reason the drift
 * correction below is here rather than a plain `Date.now()`.
 */

/**
 * How far this browser's clock is from the server's, in milliseconds.
 *
 * A laptop whose clock is five minutes fast would otherwise show a counter five
 * minutes ahead of the hours that end up recorded, and the operator would have
 * no way to tell which of the two numbers to believe. Machines being minutes out
 * is ordinary; being hours out (a wrong timezone written into the system clock,
 * a VM resumed from a snapshot) is not rare either.
 *
 * The measurement carries the response's own travel time inside it, so the
 * figure is late by roughly one network round trip. That is a fraction of a
 * second against a counter that ticks in seconds, and the alternative - timing
 * the request and halving it - is more arithmetic in exchange for precision no
 * screen here can show.
 */
export function clockSkewMs(serverNow: string, receivedAtMs: number = Date.now()): number {
  const server = Date.parse(serverNow);
  if (!Number.isFinite(server)) return 0;
  return server - receivedAtMs;
}

/**
 * Whole seconds a clock has been running.
 *
 * Never negative. A start in the future means the two clocks disagree by more
 * than the correction caught, and a counter running backwards from a negative
 * number is a screen nobody can read; zero at least says "just started".
 */
export function elapsedSeconds(startedAt: string, skewMs = 0, nowMs: number = Date.now()): number {
  const started = Date.parse(startedAt);
  if (!Number.isFinite(started)) return 0;
  return Math.max(0, Math.floor((nowMs + skewMs - started) / 1000));
}

const pad = (n: number) => String(n).padStart(2, "0");

/**
 * The counter itself: `MM:SS` under an hour, `H:MM:SS` above one.
 *
 * The hour is NOT zero-padded and the minutes always are, which is how a clock
 * is read aloud ("one oh five") and how every stopwatch prints it. Padding the
 * hour would put a leading zero in front of most of the day's work; leaving the
 * minutes unpadded would make the field change width twice a minute, and it sits
 * in the top bar next to things that must not move.
 */
export function fmtElapsed(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return h > 0 ? `${h}:${pad(m)}:${pad(s % 60)}` : `${pad(m)}:${pad(s % 60)}`;
}

/**
 * The same span in whole minutes, rounded the way the server rounds it when it
 * writes the entry (`crm-projects/lib/timer.ts`), so the dialog that asks "how
 * much of this was work" opens with the figure that would have been recorded
 * had it not asked - never one minute off it.
 */
export function elapsedToMinutes(seconds: number): number {
  return Math.max(1, Math.round(Math.max(0, seconds) / 60));
}
